import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { styled } from '@glitz/react';
import { ButtonHeight, ButtonTheme, SolidButton } from 'Shared/TextButtons';
import VideoSourceType from 'Shared/ViewModels/VideoSourceViewModel.type';
import * as style from 'Shared/Style';
import { ViewportSizeVideoBackground } from 'Shared/SharedComponents/molecules/ImageVideoBackground';
import { OverlayNotABackdrop } from 'Shared/SharedComponents/atoms/AspectRatio';
import SoundControls from './SoundControls';
import TimeControls from './TimeControls';
import PlayControls from './PlayControls';
import MaximizeControls from './MaximizeControls';
import { Plus12x12 } from 'Shared/Icons/Plus';
import VideoPlayer from 'Shared/Video/VideoPlayer'
import { Preset } from 'Shared/image-sizes';

type PropType = {
  videoSources: VideoSourceType[];
  previewImage: { url?: string };
  previewImagePreset?: Preset;
  previewVideoSources?: VideoSourceType[];
  isCompact: boolean;
  children?: ReactNode;
  videoStarted: boolean;
  stopVideo?: () => void;
  loop?: boolean;
  muted?: boolean;
  buttonsInControls?: { text: string; onClick: () => void }[];
};

const Wrap = styled.div({
  position: 'relative', height: '100%', overflow: 'hidden', alignSelf: 'normal'
})

const VideoStopped = styled.div({
  display: 'flex',
  height: '100%',
  width: '100%',
  position: 'absolute',
  top: 0,
  flexDirection: 'column',
  color: style.colors.monochrome.white,
})

const VideoStartedWrap = styled.div({
  height: '100%',
  width: '100%',
  position: 'absolute',
  top: 0,
  paddingBottom: '40px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'end',
  color: style.colors.monochrome.white,
  gap: '32px',
})

const VideoStartedWrapDesktop = styled(VideoStartedWrap, {
  gap: '16px',
})

const TimeControlWrapMobile = styled.div({
  width: '100%',
  padding: { x: '24px' },
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const ButtonsWrapDesktop = styled.div({
  width: '100%',
  padding: { x: '40px' },
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
  justifyContent: 'end',
})

const ButtonsWrapMobile = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
})

const PlusIcon = styled(Plus12x12, {
  marginLeft: '8px'
})

const OuterBase = styled.div({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  gap: '24px',
  alignItems: 'center',
})

const OuterMobile = styled(OuterBase, {
  padding: { x: '32px' },
  justifyContent: 'space-between',
})

const OuterDesktop = styled(OuterBase, {
  padding: { x: '40px' },
})

const Inner = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: '24px',
  alignItems: 'center',
})

const Video = ({
  videoSources,
  previewImage,
  previewImagePreset,
  previewVideoSources,
  isCompact,
  videoStarted,
  stopVideo = () => { },
  children,
  loop = false,
  muted = false,
  buttonsInControls,
}: PropType) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const play = () => {
    videoRef.current?.play();
    setIsPlaying(true);
  };

  const pause = () => {
    videoRef.current?.pause();
    setIsPlaying(false);
  };

  useEffect(() => {
    if (videoStarted) {
      play();
    }
  }, [videoStarted]);

  const VideoStartedWrapElement = isCompact ? VideoStartedWrap : VideoStartedWrapDesktop;
  const Outer = isCompact ? OuterMobile : OuterDesktop;
  return (
    <Wrap>
      <ViewportSizeVideoBackground
        image={previewImage}
        preset={previewImagePreset}
        videoSources={videoStarted ? null : previewVideoSources}
        breakPoint={isCompact ? 0 : 1}
        height={'100%'}
      >
        {videoStarted && (
          <OverlayNotABackdrop>
            <VideoPlayer
              autoPlay
              videoRef={videoRef}
              loop={loop}
              muted={muted}
              coverContent
              playsInline={isCompact}
              isMobile={isCompact}
              sources={videoSources}
              onEnded={() => stopVideo()}
            />
          </OverlayNotABackdrop>
        )}
        {!videoStarted && (
          <VideoStopped>
            {children}
          </VideoStopped>
        )}
        {videoStarted && !loop && (
          <VideoStartedWrapElement
            id="video-controls"
            className="controls"
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                if (isPlaying) {
                  pause();
                } else {
                  play();
                }
              }
            }}
          >
            {!isCompact && buttonsInControls && buttonsInControls.length > 0 && (
              <ButtonsWrapDesktop>
                {buttonsInControls.map((buttonInControls, index) => {
                  return (
                    <SolidButton
                      key={index}
                      text={buttonInControls.text}
                      onClick={() => buttonInControls.onClick()}
                      iconAfter={<PlusIcon />}
                      padding={{ x: 16 }}
                      height={ButtonHeight.Video}
                      theme={ButtonTheme.TransparentWhite}
                    />
                  );
                })}
              </ButtonsWrapDesktop>
            )}
            <Outer>
              <Inner>
                <PlayControls isPlaying={isPlaying} play={play} pause={pause} />
                <SoundControls mediaRef={videoRef} />
                {isCompact && buttonsInControls && buttonsInControls.length > 0 && (
                  <ButtonsWrapMobile>
                    {buttonsInControls.map((buttonInControls, index) => {
                      return (
                        <SolidButton
                          key={index}
                          text={buttonInControls.text}
                          onClick={() => buttonInControls.onClick()}
                          iconAfter={<PlusIcon />}
                          padding={{ x: 16 }}
                          height={ButtonHeight.Video}
                          theme={ButtonTheme.TransparentWhite}
                        />
                      );
                    })}
                  </ButtonsWrapMobile>
                )}
              </Inner>
              {!isCompact && <TimeControls mediaRef={videoRef} />}
              <MaximizeControls mediaRef={videoRef} isMaximized={isMaximized} updateIsMaximized={setIsMaximized} />
            </Outer>
            {isCompact && (
              <TimeControlWrapMobile>
                <TimeControls mediaRef={videoRef} />
              </TimeControlWrapMobile>
            )}
          </VideoStartedWrapElement>
        )}
      </ViewportSizeVideoBackground>
    </Wrap>
  );
};

export default Video;